import { apiDelete, apiGet, apiPost, apiPut } from './client';

export type Contact = {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  tags: string | null;
  deal_count: number;
  created_at: number;
};

export type Lead = {
  id: string;
  contact_id: string;
  status: string;
  source: string | null;
  score: number;
  notes: string | null;
  created_at: number;
  first_name: string;
  last_name: string;
  email: string;
};

export type Deal = {
  id: string;
  contact_id: string;
  stage_id: string;
  value: number;
  name: string;
  probability: number;
  close_date: number | null;
  created_at: number;
  stage_name: string;
  first_name: string;
  last_name: string;
};

export type Task = {
  id: string;
  assignee_id: string | null;
  related_contact_id: string | null;
  title: string;
  description: string | null;
  status: string;
  due_date: number | null;
  due_time: number | null;
  assignee_email: string | null;
  first_name: string | null;
  last_name: string | null;
  contact_email: string | null;
};

export type AutomationRule = {
  id: string;
  name: string;
  trigger_type: string;
  trigger_config: string;
  action_type: string;
  action_config: string;
  is_active: number;
  created_at: number;
};

export type AnalyticsSummary = {
  leads: number;
  dealsWon: number;
  revenue: number;
  tasksCompleted: number;
};

export function fetchContacts() {
  return apiGet<Contact[]>('/contacts');
}

export function createContact(payload: {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  tags?: string[];
}) {
  return apiPost<{ id: string }>('/contacts', payload);
}

export function updateContact(id: string, payload: Partial<{
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  tags: string[];
}>) {
  return apiPut<{ message: string }>(`/contacts/${id}`, payload);
}

export function deleteContact(id: string) {
  return apiDelete<{ message: string }>(`/contacts/${id}`);
}

export function fetchLeads() {
  return apiGet<Lead[]>('/leads');
}

export function createLead(payload: {
  contactId: string;
  status?: string;
  source?: string;
  score?: number;
  notes?: string;
}) {
  return apiPost<{ id: string }>('/leads', payload);
}

export function updateLead(id: string, payload: Partial<{
  status: string;
  source: string;
  score: number;
  notes: string;
}>) {
  return apiPut<{ message: string }>(`/leads/${id}`, payload);
}

export function fetchDeals() {
  return apiGet<Deal[]>('/deals');
}

export function createDeal(payload: {
  contactId: string;
  stageId: string;
  value: number;
  name: string;
  probability?: number;
  closeDate?: number;
}) {
  return apiPost<{ id: string }>('/deals', payload);
}

export function updateDeal(id: string, payload: Partial<{
  stageId: string;
  value: number;
  name: string;
  probability: number;
  closeDate: number | null;
}>) {
  return apiPut<{ message: string }>(`/deals/${id}`, payload);
}

export function fetchTasks() {
  return apiGet<Task[]>('/tasks');
}

export function createTask(payload: {
  title: string;
  description?: string;
  assigneeId?: string;
  relatedContactId?: string;
  dueDate?: number;
  dueTime?: number;
}) {
  return apiPost<{ id: string }>('/tasks', payload);
}

export function updateTask(id: string, payload: Partial<{
  title: string;
  description: string;
  status: string;
  assigneeId: string | null;
  dueDate: number | null;
  dueTime: number | null;
}>) {
  return apiPut<{ message: string }>(`/tasks/${id}`, payload);
}

export function fetchAutomations() {
  return apiGet<AutomationRule[]>('/automations');
}

export function createAutomation(payload: {
  name: string;
  triggerType: string;
  triggerConfig: Record<string, unknown>;
  actionType: string;
  actionConfig: Record<string, unknown>;
  isActive: boolean;
}) {
  return apiPost<{ id: string }>('/automations', payload);
}

export function updateAutomation(id: string, payload: Partial<{
  name: string;
  triggerType: string;
  triggerConfig: Record<string, unknown>;
  actionType: string;
  actionConfig: Record<string, unknown>;
  isActive: boolean;
}>) {
  return apiPut<{ message: string }>(`/automations/${id}`, payload);
}

export function fetchAnalyticsSummary() {
  return apiGet<AnalyticsSummary>('/analytics/summary');
}
